import { useEffect, useState } from 'react';
import type { CreatePrinterLibraryInput } from '@maker-toolchest/shared';
import { createPrinterLibrarySchema } from '@maker-toolchest/shared';
import { SlideOver } from '@/components/SlideOver';
import { Input } from '@/components/Input';
import { ComboBox } from '@/components/ComboBox';
import { Textarea } from '@/components/Textarea';
import { FormSection } from '@/components/FormSection';
import { Button } from '@/components/Button';
import { Alert } from '@/components/Alert';
import { usePrinterLookups, useCreatePrinterBrand, useCreatePrinterModel } from '../hooks';
import { extractFieldErrors, getErrorMessage, isValidationError, zodErrorsToFieldErrors } from '@/utils/formErrors';

interface CustomPrinterLibraryFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreatePrinterLibraryInput) => Promise<void>;
  loading?: boolean;
  error?: Error | null; // API error to display
}

// Form data type with number strings (from HTML inputs)
type FormData = Omit<
  Partial<CreatePrinterLibraryInput>,
  | 'buildVolumeX'
  | 'buildVolumeY'
  | 'buildVolumeZ'
  | 'nozzleTempMax'
  | 'bedTempMax'
  | 'chamberTempMax'
  | 'maxPrintSpeed'
  | 'maxTravelSpeed'
  | 'defaultNozzleSlots'
  | 'defaultHotendSlots'
  | 'defaultSpoolSlots'
  | 'defaultAccessorySlots'
> & {
  buildVolumeX?: string;
  buildVolumeY?: string;
  buildVolumeZ?: string;
  nozzleTempMax?: string;
  bedTempMax?: string;
  chamberTempMax?: string;
  maxPrintSpeed?: string;
  maxTravelSpeed?: string;
  defaultNozzleSlots: string;
  defaultHotendSlots: string;
  defaultSpoolSlots: string;
  defaultAccessorySlots: string;
};

const defaultFormData: FormData = {
  name: '',
  brandId: '',
  modelId: '',
  technology: 'FDM',
  defaultNozzleSlots: '1',
  defaultHotendSlots: '1',
  defaultSpoolSlots: '1',
  defaultAccessorySlots: '0',
  notes: '',
};

export function CustomPrinterLibraryForm({
  open,
  onClose,
  onSubmit,
  loading = false,
  error,
}: CustomPrinterLibraryFormProps) {
  const [formData, setFormData] = useState<FormData>(defaultFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { brands, models, isLoading: lookupsLoading } = usePrinterLookups();
  const createBrand = useCreatePrinterBrand();
  const createModel = useCreatePrinterModel();

  // Reset form when opening/closing
  useEffect(() => {
    if (open) {
      setFormData(defaultFormData);
      setErrors({});
    }
  }, [open]);

  // Extract field errors from API error
  useEffect(() => {
    if (error) {
      const fieldErrors = extractFieldErrors(error);
      setErrors(fieldErrors);
    } else {
      setErrors({});
    }
  }, [error]);

  const handleChange = (field: keyof FormData, value: unknown) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Clear modelId when brand changes
      if (field === 'brandId' && value !== prev.brandId) {
        next.modelId = '';
      }
      return next;
    });

    // Clear error for this field when user makes a change
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleCreateBrand = async (name: string) => {
    const newBrand = await createBrand.mutateAsync({ name });
    setFormData((prev) => ({ ...prev, brandId: newBrand.id, modelId: '' }));
  };

  const handleCreateModel = async (name: string) => {
    if (!formData.brandId) {
      throw new Error('Please select a brand first');
    }
    const newModel = await createModel.mutateAsync({ name, brandId: formData.brandId });
    setFormData((prev) => ({ ...prev, modelId: newModel.id }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    try {
      // Convert string numbers to numbers or null
      // Convert empty strings to null for brandId/modelId (None option)
      const submitData: CreatePrinterLibraryInput = {
        name: formData.name!,
        brandId: formData.brandId || null,
        modelId: formData.modelId || null,
        technology: formData.technology || 'FDM',
        buildVolumeX: formData.buildVolumeX ? parseInt(formData.buildVolumeX, 10) : undefined,
        buildVolumeY: formData.buildVolumeY ? parseInt(formData.buildVolumeY, 10) : undefined,
        buildVolumeZ: formData.buildVolumeZ ? parseInt(formData.buildVolumeZ, 10) : undefined,
        nozzleTempMax: formData.nozzleTempMax ? parseInt(formData.nozzleTempMax, 10) : undefined,
        bedTempMax: formData.bedTempMax ? parseInt(formData.bedTempMax, 10) : undefined,
        chamberTempMax: formData.chamberTempMax ? parseInt(formData.chamberTempMax, 10) : undefined,
        maxPrintSpeed: formData.maxPrintSpeed ? parseInt(formData.maxPrintSpeed, 10) : undefined,
        maxTravelSpeed: formData.maxTravelSpeed ? parseInt(formData.maxTravelSpeed, 10) : undefined,
        defaultNozzleSlots: parseInt(formData.defaultNozzleSlots, 10),
        defaultHotendSlots: parseInt(formData.defaultHotendSlots, 10),
        defaultSpoolSlots: parseInt(formData.defaultSpoolSlots, 10),
        defaultAccessorySlots: parseInt(formData.defaultAccessorySlots, 10),
        notes: formData.notes || undefined,
      };

      // Validate with Zod schema
      const result = createPrinterLibrarySchema.safeParse(submitData);

      if (!result.success) {
        const validationErrors = zodErrorsToFieldErrors(result.error);
        setErrors(validationErrors);
        return;
      }

      await onSubmit(submitData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  // Build options for ComboBoxes
  const brandOptions = [
    { value: '', label: 'None (Custom Build)' },
    ...brands.map((brand) => ({ value: brand.id, label: brand.name })),
  ];

  // Filter models by selected brand
  const filteredModels = formData.brandId
    ? models.filter((model) => model.brandId === formData.brandId)
    : models;
  const modelOptions = [
    { value: '', label: 'None (Custom Build)' },
    ...filteredModels.map((model) => ({ value: model.id, label: model.name })),
  ];

  const technologyOptions = [
    { value: 'FDM', label: 'FDM (Fused Deposition Modeling)' },
    { value: 'SLA', label: 'SLA (Stereolithography)' },
    { value: 'SLS', label: 'SLS (Selective Laser Sintering)' },
    { value: 'MJF', label: 'MJF (Multi Jet Fusion)' },
    { value: 'BINDER_JET', label: 'Binder Jet' },
  ];

  return (
    <SlideOver open={open} onClose={onClose} title="Create Custom Printer">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {/* API Error Banner */}
        {error && !isValidationError(error) && (
          <Alert
            variant="error"
            title="Error"
            message={getErrorMessage(error)}
          />
        )}
        {error && isValidationError(error) && (
          <Alert
            variant="error"
            title="Validation Error"
            message="Please fix the errors below"
          />
        )}

        <FormSection title="Basic Information">
          <Input
            label="Printer Name"
            value={formData.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            error={errors.name}
            placeholder="e.g., Modified Ender 3"
            required
          />

          <ComboBox
            label="Brand"
            value={formData.brandId || ''}
            onChange={(value) => handleChange('brandId', value)}
            onCreateNew={handleCreateBrand}
            error={errors.brandId}
            disabled={lookupsLoading}
            options={brandOptions}
            placeholder="Select or create a brand..."
            createLabel="Create brand"
          />

          <ComboBox
            label="Model"
            value={formData.modelId || ''}
            onChange={(value) => handleChange('modelId', value)}
            onCreateNew={handleCreateModel}
            error={errors.modelId}
            disabled={lookupsLoading || !formData.brandId}
            options={modelOptions}
            placeholder="Select or create a model..."
            createLabel="Create model"
            hint={!formData.brandId ? 'Select a brand first' : undefined}
          />

          <ComboBox
            label="Technology"
            value={formData.technology || 'FDM'}
            onChange={(value) => handleChange('technology', value)}
            options={technologyOptions}
            placeholder="Select a technology..."
          />
        </FormSection>

        <FormSection title="Build Volume (mm)">
          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Width (X)"
              type="number"
              value={formData.buildVolumeX || ''}
              onChange={(e) => handleChange('buildVolumeX', e.target.value)}
              placeholder="220"
              min="1"
              max="2000"
            />
            <Input
              label="Depth (Y)"
              type="number"
              value={formData.buildVolumeY || ''}
              onChange={(e) => handleChange('buildVolumeY', e.target.value)}
              placeholder="220"
              min="1"
              max="2000"
            />
            <Input
              label="Height (Z)"
              type="number"
              value={formData.buildVolumeZ || ''}
              onChange={(e) => handleChange('buildVolumeZ', e.target.value)}
              placeholder="250"
              min="1"
              max="2000"
            />
          </div>
        </FormSection>

        <FormSection title="Temperature Capabilities (°C)">
          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Max Nozzle Temp"
              type="number"
              value={formData.nozzleTempMax || ''}
              onChange={(e) => handleChange('nozzleTempMax', e.target.value)}
              placeholder="300"
              min="1"
              max="500"
            />
            <Input
              label="Max Bed Temp"
              type="number"
              value={formData.bedTempMax || ''}
              onChange={(e) => handleChange('bedTempMax', e.target.value)}
              placeholder="100"
              min="1"
              max="200"
            />
            <Input
              label="Max Chamber Temp"
              type="number"
              value={formData.chamberTempMax || ''}
              onChange={(e) => handleChange('chamberTempMax', e.target.value)}
              placeholder="60"
              min="1"
              max="100"
            />
          </div>
        </FormSection>

        <FormSection title="Speed Capabilities (mm/s)">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Max Print Speed"
              type="number"
              value={formData.maxPrintSpeed || ''}
              onChange={(e) => handleChange('maxPrintSpeed', e.target.value)}
              placeholder="200"
              min="1"
              max="1000"
            />
            <Input
              label="Max Travel Speed"
              type="number"
              value={formData.maxTravelSpeed || ''}
              onChange={(e) => handleChange('maxTravelSpeed', e.target.value)}
              placeholder="300"
              min="1"
              max="1000"
            />
          </div>
        </FormSection>

        <FormSection title="Default Slot Configuration">
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Nozzle Slots"
              type="number"
              value={formData.defaultNozzleSlots}
              onChange={(e) => handleChange('defaultNozzleSlots', e.target.value)}
              min="1"
              max="10"
              required
            />
            <Input
              label="Hotend Slots"
              type="number"
              value={formData.defaultHotendSlots}
              onChange={(e) => handleChange('defaultHotendSlots', e.target.value)}
              min="1"
              max="10"
              required
            />
            <Input
              label="Spool Slots"
              type="number"
              value={formData.defaultSpoolSlots}
              onChange={(e) => handleChange('defaultSpoolSlots', e.target.value)}
              min="1"
              max="20"
              required
            />
            <Input
              label="Accessory Slots"
              type="number"
              value={formData.defaultAccessorySlots}
              onChange={(e) => handleChange('defaultAccessorySlots', e.target.value)}
              min="0"
              max="10"
              required
            />
          </div>
        </FormSection>

        <FormSection title="Notes">
          <Textarea
            label="Additional Notes"
            value={formData.notes || ''}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Custom modifications, special features, etc."
            rows={3}
          />
        </FormSection>

        <div className="flex justify-end gap-2 pt-4 border-t border-border">
          <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={loading}>
            Create Printer
          </Button>
        </div>
      </form>
    </SlideOver>
  );
}
